/**
 * System: Eingabe.
 *
 * Zuständigkeit: Absichten in Spielerzustand übersetzen. Sonst nichts.
 *
 * Wichtig (Ueberarbeitung A2): Die gewollte Laufrichtung hat **genau einen
 * Besitzer** — `spieler.richtung`. Der Client hält keine zweite mehr. Vorher
 * lief beides auseinander, mit zwei Folgen: Auto-Lauf ließ sich nicht beenden,
 * und WASD wirkte nur, solange die Taste gedrückt war.
 */

import type { Welt } from '../typen.js';
import type { Ctx } from './typen.js';
import { abstand, laenge, normiere, vek } from '../vektor.js';

export function eingabe(welt: Welt, ctx: Ctx): void {
  const s = welt.spieler;

  // Standardmäßig steuert niemand. Erst eine Bewegungsabsicht setzt das.
  s.steuertGerade = false;

  for (const a of ctx.absichten) {
    switch (a.art) {
      case 'bewegen': {
        if (a.richtung && laenge(a.richtung) > 0.08) {
          s.richtung = normiere(a.richtung);
          s.steuertGerade = true;
          // Wer steuert, hebt den Anker auf — sonst wundert man sich,
          // warum die Figur stehen bleibt.
          s.anker = false;
        } else if (!s.autolauf) {
          // Ohne Auto-Lauf bedeutet "keine Eingabe" auch "anhalten".
          s.richtung = vek(0, 0);
        }
        break;
      }

      case 'autolauf': {
        s.autolauf = a.an;
        if (!a.an) {
          // Ausschalten muss auch WIRKLICH anhalten. Genau hier saß der Fehler:
          // die alte Richtung blieb stehen und die Figur lief ewig weiter.
          s.richtung = vek(0, 0);
        } else if (laenge(s.richtung) < 0.08) {
          // Einschalten aus dem Stand: in die Blickrichtung losgehen. Wer den
          // Knopf drueckt, erwartet loszulaufen — nicht, erst noch steuern zu
          // muessen, damit der Schalter etwas tut.
          s.richtung = vek(Math.cos(s.winkel), Math.sin(s.winkel));
          s.anker = false;
        }
        break;
      }

      case 'anker': {
        s.anker = a.an;
        if (a.an) s.richtung = vek(0, 0);
        break;
      }

      case 'automatikgrad': {
        s.automatikgrad = a.grad;
        break;
      }

      case 'handbetrieb': {
        const slot = s.slots[a.slot];
        if (slot) slot.handbetrieb = a.an;
        break;
      }

      case 'faehigkeit': {
        const slot = s.slots[a.slot];
        if (slot) {
          slot.manuellAngefordert = true;
          slot.manuellZiel = a.ziel;
        }
        break;
      }

      case 'waehleFaehigkeit': {
        waehleFaehigkeit(welt, ctx, a.faehigkeit);
        break;
      }

      case 'einsteigen': {
        welt.einsteigen = true;
        break;
      }

      case 'tempoprofil': {
        welt.tempoprofil = a.profil;
        break;
      }

      case 'aufheben': {
        // Beute wird ANGEKLICKT, nicht im Vorbeigehen genommen (Wunsch aus
        // dem zweiten Spieltest). Nur was in Reichweite liegt.
        const b = welt.beute.find((x) => x.id === a.id && !x.aufgehoben);
        if (!b) break;
        if (abstand(b.pos, s.pos) > welt.aufhebeReichweite) break;
        b.aufgehoben = true;
        welt.ertrag.stuecke++;
        ctx.ereignisse.push({
          art: 'beute-aufgehoben',
          id: b.id,
          name: b.name,
          durchBegleiter: false,
        });
        ctx.effekte.push({ art: 'aufgehoben', pos: { ...b.pos }, wert: 1, takt: welt.takt });
        break;
      }
    }
  }
}

/**
 * Der Spieler hat sich bei einer Freischaltung entschieden (Ueberarbeitung B10).
 * Die Fähigkeit wird freigeschaltet und auf den nächsten freien Platz gelegt.
 */
function waehleFaehigkeit(welt: Welt, ctx: Ctx, id: string): void {
  const s = welt.spieler;
  if (!s.offeneWahl || !s.offeneWahl.includes(id)) return;

  const f = ctx.inhalte.faehigkeiten.get(id);
  if (!f) return;

  s.freigeschaltet.push(id);
  s.offeneWahl = null;

  const plaetze = ctx.inhalte.regelwerk.faehigkeiten.plaetze;
  if (s.slots.length < plaetze) {
    s.slots.push({
      faehigkeit: f,
      // Bewegungsfähigkeiten starten auf Handbetrieb — sie versetzen den
      // Spieler, und das gehört ihm (Ueberarbeitung B9a).
      handbetrieb: (f.eigenbewegung ?? 0) > 0,
      abklingen: 0,
      manuellAngefordert: false,
      manuellZiel: null,
      regelAktiv: false,
    });
  }

  ctx.ereignisse.push({ art: 'faehigkeit-freigeschaltet', faehigkeit: id, name: f.name });
}
